import type { CSSProperties } from "react";

/* Color variations for the pattern-2 homepage, built from the team's palette.
   Primaries: periwinkle #C0C7FB, golden #F0BE3C, deep teal #145A50,
   coral #E66E78, cream #FBF3E4. Secondaries: poppy #C83214, magenta #DC46A0,
   blush #F0B0B0, cobalt #145A82, olive #A0961E. Ink #0B302A is a darker deep
   teal for body text, so it reads on every fill (4.6:1 on coral and up).

   Every color on the page comes from one of these roles (CSS var --k-<role>).
   Contrast rules the roles must keep, measured against `paper`:
   - ink, accent: small text, 4.5:1 or more
   - h1, h2, h3: big display text only, 3:1 or more
   - each fill's *Ink partner: 4.5:1 or more on that fill */

export type Palette = {
  key: string; label: string;
  paper: string; card: string; ink: string;
  h1: string; h2: string; h3: string; accent: string;
  strong: string; strongInk: string;   // main band (club), primary buttons
  strongPop: string;                   // highlighted words on the strong band, 3:1 or more on strong
  soft: string; softInk: string;       // second band (kind words), hero shape
  pop: string; popInk: string;         // marker underline, torn scraps, third band
  extra: string; extraInk: string;     // small fills: pattern envelope header, tags on fills
  cta: string; ctaInk: string;         // nav button
  foot: string; footInk: string;       // footer
  live: string;                        // "Live" badge, with paper-colored text
  thread: string;                      // dashed thread lines
};

const BASE = { paper: "#FBF3E4", card: "#FFFAF1", ink: "#0B302A", h3: "#0B302A", live: "#C83214" };

export const PALETTES: Palette[] = [
  { ...BASE, key: "teal-coral", label: "Teal & Coral",
    h1: "#145A50", h2: "#C83214", accent: "#C83214",
    strong: "#145A50", strongInk: "#FBF3E4", strongPop: "#F0BE3C", soft: "#E66E78", softInk: "#0B302A",
    pop: "#F0BE3C", popInk: "#0B302A", extra: "#C0C7FB", extraInk: "#0B302A",
    cta: "#E66E78", ctaInk: "#0B302A", foot: "#145A50", footInk: "#FBF3E4", thread: "#145A50" },
  { ...BASE, key: "periwinkle", label: "Periwinkle",
    h1: "#145A82", h2: "#DC46A0", accent: "#145A82",
    strong: "#C0C7FB", strongInk: "#0B302A", strongPop: "#C83214", soft: "#F0B0B0", softInk: "#0B302A",
    pop: "#F0BE3C", popInk: "#0B302A", extra: "#145A82", extraInk: "#FBF3E4",
    cta: "#F0BE3C", ctaInk: "#0B302A", foot: "#145A82", footInk: "#FBF3E4", thread: "#145A82" },
  { ...BASE, key: "golden", label: "Golden",
    h1: "#145A50", h2: "#C83214", accent: "#C83214",
    strong: "#F0BE3C", strongInk: "#0B302A", strongPop: "#C83214", soft: "#C0C7FB", softInk: "#0B302A",
    pop: "#E66E78", popInk: "#0B302A", extra: "#145A50", extraInk: "#FBF3E4",
    cta: "#145A50", ctaInk: "#FBF3E4", foot: "#145A50", footInk: "#FBF3E4", thread: "#145A50" },
  { ...BASE, key: "secondaries", label: "Bold secondaries",
    h1: "#145A82", h2: "#DC46A0", accent: "#C83214",
    strong: "#145A82", strongInk: "#FBF3E4", strongPop: "#F0BE3C", soft: "#F0B0B0", softInk: "#0B302A",
    pop: "#F0BE3C", popInk: "#0B302A", extra: "#C83214", extraInk: "#FBF3E4",
    cta: "#A0961E", ctaInk: "#0B302A", foot: "#145A82", footInk: "#FBF3E4", thread: "#A0961E" },
];

const ROLES = ["paper", "card", "ink", "h1", "h2", "h3", "accent", "strong", "strongInk", "strongPop", "soft", "softInk", "pop", "popInk", "extra", "extraInk", "cta", "ctaInk", "foot", "footInk", "live", "thread"] as const;

/** The palette as --k-* custom properties for the page root. */
export function paletteStyle(p: Palette): CSSProperties {
  const kebab = (s: string) => s.replace(/[A-Z]/g, c => `-${c.toLowerCase()}`);
  return Object.fromEntries(ROLES.map(r => [`--k-${kebab(r)}`, p[r]])) as CSSProperties;
}
